import React, { useContext, useState, useEffect } from "react";
import { 
    Grid, 
    FormHelperText, 
    TextField, 
    Typography, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    MenuItem, 
    FormControl, 
    Select,
    Link as LinkIcon 
} from "@leap4/frontend";
import { IntegrationContext } from "./base/integrationContext";
import { Link } from "react-router-dom";
import { urlService } from "../../routing/urlsService";


export interface IIntegrationFormProps {
    mode: "create" | "edit";
}

const IntegrationForm = (props: IIntegrationFormProps) => {
    const {
        integration,
        setIntegration,
        demandWLTemplates,
    } = useContext(IntegrationContext);
    const [openCredentials, setOpenCredentials] = useState(false);
    const [credentials, setCredentials] = useState({ 
        username: '',
        password: ''
    });

    // Initialize credentials from integration
    useEffect(() => {
        setCredentials({
            username: '',
            password: ''
        });
    }, [integration]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setIntegration({...integration, [name]: value});
    }

    const onCredentialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
    }

    const handleSaveCredentials = () => {
        setIntegration({
            ...integration,
            username: credentials.username,
            password: credentials.password
        });
        setOpenCredentials(false);
    }

    return (
        <>
            <Typography fontFamily={"Quicksand"} fontWeight={600} variant="h5" style={{ paddingBottom: "20px" }}>
                {props.mode === "edit" ? "Edit Integration Details" : "New Integration Details"}
            </Typography>

            <Grid container direction={"row"} justifyContent={"flex-start"} alignItems={"center"} gap={"20px"}>
                <Grid item xs={1.8} md={1.8}>
                    <FormHelperText>
                        Name
                    </FormHelperText>
                    <TextField
                        autoFocus
                        fullWidth
                        value={integration.name || ''}
                        onChange={onChange}
                        size="medium"
                        variant="outlined"
                        name="name"
                        className={"gridItem"}
                    />
                </Grid>
                <Grid item xs={1.8} md={1.8}>
                    <FormHelperText>
                        Ad Server
                    </FormHelperText>
                    <FormControl fullWidth size="medium" variant="outlined">
                        <Select
                            value={integration.adserver || 'xroads'}
                            onChange={(e) => onChange(e as any)}
                            name="adserver"
                            className={"gridItem"}
                        >
                            <MenuItem value="xroads">xroads</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={1.8} md={1.8}>
                    <FormHelperText>
                        Host
                    </FormHelperText>
                    <TextField
                        fullWidth
                        value={integration.host || ''}
                        onChange={onChange}
                        style={{ width: '500px' }}
                        variant="outlined"
                        name="host"
                        className={"gridItem"}
                    />
                </Grid>
                <Grid item xs={8} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button variant="contained" onClick={() => setOpenCredentials(true)} sx={{ height: 'auto', width: 'auto', px: 3 }}>
                        {props.mode === "edit" ? "Change Integration Credentials" : "Add Integration Credentials"}
                    </Button>
                </Grid>
            </Grid>
            <Grid container direction={"row"} justifyContent={"flex-start"} alignItems={"flex-start"} gap={"20px"}>
                <Grid item xs={8}>
                    {/* Your existing form fields */}
                </Grid>
                <Grid item xs={4}>
                    <Typography 
                        variant="h6" 
                        style={{ 
                            paddingTop: "20px",
                            borderBottom: '2px solid #1976d2',
                            marginBottom: '16px'
                        }}
                    >
                        Associated Templates
                    </Typography>
                    {Object.entries(demandWLTemplates).length > 0 ? (
                        Object.entries(demandWLTemplates).map(([id, name]) => (
                            <Typography 
                                key={id} 
                                variant="body1" 
                                sx={{
                                    marginBottom: '8px',
                                    '& a': {
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        '&:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }
                                }}
                            >
                                <Link 
                                    to={`${urlService.demandWLGenerator()}?id=${id}`}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}
                                >
                                    <LinkIcon sx={{ marginRight: 1, fontSize: 20 }} />
                                    {name}
                                </Link>
                            </Typography>
                        ))
                    ) : (
                        <Typography 
                            variant="body1" 
                            sx={{ 
                                color: 'text.secondary',
                                fontStyle: 'italic'
                            }}
                        >
                            No associated templates
                        </Typography>
                    )}
                </Grid>
            </Grid>
            <Dialog open={openCredentials} onClose={() => setOpenCredentials(false)}>
                <DialogTitle>Credentials</DialogTitle>
                <DialogContent>
                    <Grid container direction={"column"} spacing={2} sx={{ pt: 1 }}>
                        <Grid item>
                            <FormHelperText>Username</FormHelperText>
                            <TextField
                                fullWidth
                                value={credentials.username}
                                onChange={onCredentialChange}
                                size="medium"
                                variant="outlined"
                                name="username"
                                className={"gridItem"}
                            />
                        </Grid>
                        <Grid item>
                            <FormHelperText>Password</FormHelperText>
                            <TextField
                                fullWidth
                                type="password"
                                value={credentials.password}
                                onChange={onCredentialChange}
                                size="medium"
                                variant="outlined"
                                name="password"
                                className={"gridItem"}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenCredentials(false)}>Cancel</Button>
                    <Button onClick={handleSaveCredentials} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default IntegrationForm;
